'use client'

import debounce from 'debounce-promise'
import React, { SyntheticEvent } from 'react'
import type { ActionMeta, MultiValue, SingleValue } from 'react-select'
import { SelectComponentsConfig, Props as StateManagerProps } from 'react-select'
import AsyncSelect from 'react-select/async'
import { useInputWrapper } from '../../../hooks/useInputWrapper'
import { Text } from '../../atoms'
import {
    ClearIndicatorReplacement,
    DropdownIndicatorReplacement,
    MultiLineOption,
    MultiValueComponent,
} from './Components'
import { OptionReplacement } from './Components/OptionReplacement'
import { useId } from 'react'
import { SelectOption, GroupedSelectOption, AsyncMultiSelectInputProps } from '../../../types/SelectInput'
import {
    getSelectInputClassNames,
    getSelectContainerClassNames,
    getCustomSelectInputStyles,
} from './Helpers/select-helpers'
import { formatGroupLabel, LoadingComponent } from './Components/AsyncComponent'
import { loadAsyncQueryOptions } from './Helpers/async-select-helpers'

export const AsyncMultiSelectInput = ({
    options = [],
    asyncQuery,
    asyncOptions,
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultInputValue = undefined,
    noOptionsMessage = 'No results',
    isInline = false,
    className = '',
    placeholder,
    autoFocus = false,
    asyncMinimumCharacters = 3,
    menuPosition = 'fixed',
    ...props
}: AsyncMultiSelectInputProps) => {
    const { onBlurHandler, onFocusHandler, classes } = useInputWrapper(false, disabled)
    const generatedId = useId()
    const selectId = id ?? generatedId

    const components: SelectComponentsConfig<SelectOption, boolean, GroupedSelectOption> = {
        MultiValue: MultiValueComponent,
        DropdownIndicator: DropdownIndicatorReplacement,
        ClearIndicator: ClearIndicatorReplacement,
        ...(!showMultiLine && !showAsTag && { Option: OptionReplacement }),
    }

    const debouncedLoadOptions: (inputValue: string) => Promise<SelectOption[] | GroupedSelectOption[]> = debounce(
        (inputValue: string) =>
            loadAsyncQueryOptions(inputValue, {
                asyncMinimumCharacters,
                asyncQuery,
                isSearchable,
            }),
        250,
    )

    const loadAsyncOptions = (): Promise<SelectOption[]> => {
        const emptyResults = new Promise<SelectOption[]>((resolve) => {
            resolve([])
        })
        return asyncOptions ? asyncOptions() : emptyResults
    }

    const selectProps: StateManagerProps<SelectOption, boolean, GroupedSelectOption> = {
        instanceId: selectId,
        id: selectId,
        inputId: selectId,
        className: getSelectInputClassNames({ isInline, isMulti: true, isSearchable, showAsTag, readOnly }),
        classNamePrefix: 'select-input',
        isDisabled: disabled,
        isMulti: true,
        closeMenuOnSelect: false,
        hideSelectedOptions: false,
        isSearchable: !readOnly ? isSearchable : false,
        onBlur: (event: SyntheticEvent) => {
            if (onBlur) onBlur(event)
            onBlurHandler()
        },
        menuPlacement: 'auto',
        components,
        styles: getCustomSelectInputStyles(showMultiLine, isSearchable),
        noOptionsMessage: () => <Text>{noOptionsMessage}</Text>,
        loadingMessage: LoadingComponent,
        ...(showMultiLine && { formatOptionLabel: MultiLineOption }),
        ...(readOnly && { menuIsOpen: false }),
        ...(placeholder ? { placeholder } : { placeholder: '' }),
        autoFocus,
    }

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
        >
            <AsyncSelect
                {...selectProps}
                isClearable
                closeMenuOnSelect={false}
                cacheOptions
                loadOptions={isSearchable ? debouncedLoadOptions : loadAsyncOptions}
                formatGroupLabel={formatGroupLabel}
                onChange={(
                    newValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
                    _actionMeta: ActionMeta<SelectOption>,
                ) => {
                    if (!onChange) return
                    if (newValue == null) {
                        onChange([])
                        return
                    }
                    onChange(Array.isArray(newValue) ? [...newValue] : [newValue])
                }}
                {...(!isSearchable && { defaultOptions: true })}
                onFocus={onFocusHandler}
                defaultInputValue={defaultInputValue}
                value={options.filter((option) =>
                    Array.isArray(value) ? value?.includes(option.value) : value === option.value,
                )}
                menuPosition={menuPosition}
                isOptionDisabled={(option: SelectOption) => !!option.disabled}
            />
        </div>
    )
}
